// src/components/StudentTransfer.jsx
// Promote/transfer Admitted students in bulk from their current class to the
// next class (e.g. Class 1-A -> Class 2-A) for the new academic year.
// Reached from the "Transfer" button on the Admission Management header.

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const CLASS_OPTIONS = [
  "LKG", "UKG",
  "Class 1", "Class 2", "Class 3", "Class 4", "Class 5",
  "Class 6", "Class 7", "Class 8", "Class 9", "Class 10",
  "Class 11", "Class 12"
];

const SECTIONS = ["A", "B", "C"];

// Class -> the class students move into next year. Class 12 has none
// (final year), so it is intentionally left out of this map.
const NEXT_CLASS = {};
CLASS_OPTIONS.forEach((c, i) => {
  if (i < CLASS_OPTIONS.length - 1) NEXT_CLASS[c] = CLASS_OPTIONS[i + 1];
});

export default function StudentTransfer() {
  const navigate = useNavigate();

  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroup, setOpenGroup] = useState(null); // "Class 1-A"
  const [selected, setSelected] = useState({});     // { [studentId]: true }
  const [toClass, setToClass] = useState("");
  const [toSection, setToSection] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [banner, setBanner] = useState(null);        // { type: 'success'|'error', text }

  const fetchAdmissions = async () => {
    setLoading(true);
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/admissions");
      if (!r.ok) throw new Error("Failed");
      setAdmissions(await r.json());
    } catch {
      setBanner({ type: "error", text: "Could not load students. Please check your connection." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmissions(); }, []);

  // Group Admitted students by "Class-Section", in CLASS_OPTIONS/section order.
  const groups = useMemo(() => {
    const map = {};
    admissions
      .filter(s => s.status === "Admitted")
      .forEach(s => {
        const cls = s.applyingClass || "—";
        const sec = s.section || "—";
        const key = `${cls}-${sec}`;
        if (!map[key]) map[key] = { class: cls, section: sec, students: [] };
        map[key].students.push(s);
      });

    return Object.values(map).sort((a, b) => {
      const ci = CLASS_OPTIONS.indexOf(a.class) - CLASS_OPTIONS.indexOf(b.class);
      if (ci !== 0) return ci;
      return SECTIONS.indexOf(a.section) - SECTIONS.indexOf(b.section);
    });
  }, [admissions]);

  const openTransfer = (group) => {
    const key = `${group.class}-${group.section}`;
    setOpenGroup(key);
    setBanner(null);
    const next = NEXT_CLASS[group.class] || "";
    setToClass(next);
    setToSection(group.section);
    const initial = {};
    group.students.forEach(s => { initial[s._id] = true; });
    setSelected(initial);
  };

  const closeTransfer = () => {
    setOpenGroup(null);
    setSelected({});
  };

  const toggleStudent = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = (group, checked) => {
    const next = {};
    group.students.forEach(s => { next[s._id] = checked; });
    setSelected(next);
  };

  const handleTransfer = async (group) => {
    const studentIds = group.students.filter(s => selected[s._id]).map(s => s._id);
    if (!studentIds.length) {
      setBanner({ type: "error", text: "Select at least one student to transfer." });
      return;
    }
    if (!toClass || !toSection) {
      setBanner({ type: "error", text: "Choose the class and section to transfer into." });
      return;
    }

    setTransferring(true);
    setBanner(null);
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/admissions/promote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromClass: group.class,
          fromSection: group.section,
          toClass,
          toSection,
          studentIds
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Transfer failed");

      setBanner({ type: "success", text: data.message || "Students transferred successfully." });
      closeTransfer();
      fetchAdmissions();
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Failed to transfer students. Please try again." });
    } finally {
      setTransferring(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div style={{ minHeight: "100vh", background: "#FFFFFF" }}>
      {/* ═══ HEADER ═══ */}
      <div style={{
        background: "linear-gradient(135deg, #0D47A1 0%, #0D47A1 50%, #0D47A1 100%)",
        padding: "14px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 4px 20px rgba(13,71,161,0.15)",
        gap: 12,
        flexWrap: "wrap"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 14, background: "#2196F3",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, boxShadow: "0 4px 12px rgba(33,150,243,0.3)", flexShrink: 0
          }}>
            <i className="fas fa-arrow-right-arrow-left" style={{ color: "#0D47A1" }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: "clamp(14px, 2vw, 16px)", color: "#FFFFFF", letterSpacing: "-0.3px" }}>
              Student Transfer
            </div>
            <div style={{ fontSize: "clamp(8px, 1vw, 9px)", color: "#2196F3", letterSpacing: "0.6px", textTransform: "uppercase", fontWeight: 600 }}>
              Promote students to the next class
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(-1)}
          style={{
            background: "linear-gradient(135deg, #90CAF9 0%, #0D47A1 100%)",
            color: "#0D47A1", border: "none", padding: "6px 14px", borderRadius: 10,
            fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex",
            alignItems: "center", gap: 6, boxShadow: "0 3px 10px rgba(13,71,161,0.45)"
          }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      <div style={{ padding: "20px 24px 60px", maxWidth: 960, margin: "0 auto" }}>
        {banner && (
          <div style={{
            background: banner.type === "success" ? "#E3F2FD" : "#FFEBEE",
            color: banner.type === "success" ? "#0D47A1" : "#C62828",
            border: `1px solid ${banner.type === "success" ? "#90CAF9" : "#EF9A9A"}`,
            borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600,
            marginBottom: 16, display: "flex", alignItems: "center", gap: 8
          }}>
            <i className={`fas ${banner.type === "success" ? "fa-check-circle" : "fa-triangle-exclamation"}`}></i>
            {banner.text}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#0D47A1", fontWeight: 600 }}>
            Loading students…
          </div>
        ) : groups.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#0D47A1", fontWeight: 600 }}>
            No admitted students found.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {groups.map(group => {
              const key = `${group.class}-${group.section}`;
              const isOpen = openGroup === key;
              const nextClass = NEXT_CLASS[group.class];
              const isFinalYear = !nextClass;

              return (
                <div key={key} style={{
                  border: "1px solid #90CAF9", borderRadius: 14, overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(13,71,161,0.08)"
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 18px", background: "#E3F2FD", flexWrap: "wrap", gap: 10
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, background: "#0D47A1",
                        color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: 12
                      }}>
                        {group.section}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, color: "#0D47A1", fontSize: 14 }}>
                          {group.class} - {group.section}
                        </div>
                        <div style={{ fontSize: 11, color: "#2196F3", fontWeight: 600 }}>
                          {group.students.length} student{group.students.length !== 1 ? "s" : ""}
                          {!isFinalYear && <> · next: {nextClass}</>}
                        </div>
                      </div>
                    </div>

                    {isFinalYear ? (
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: "#0D47A1", background: "#90CAF9",
                        padding: "6px 12px", borderRadius: 8
                      }}>
                        Final year — no further transfer
                      </span>
                    ) : (
                      <button
                        onClick={() => (isOpen ? closeTransfer() : openTransfer(group))}
                        style={{
                          background: isOpen ? "#90CAF9" : "#0D47A1",
                          color: isOpen ? "#0D47A1" : "#FFFFFF",
                          border: "none", padding: "8px 16px", borderRadius: 10,
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 6
                        }}
                      >
                        <i className={`fas ${isOpen ? "fa-times" : "fa-arrow-right-arrow-left"}`}></i>
                        {isOpen ? "Cancel" : "Transfer"}
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <div style={{ padding: 18, background: "#FFFFFF" }}>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#0D47A1", display: "block", marginBottom: 4 }}>
                            Transfer to Class
                          </label>
                          <select
                            value={toClass}
                            onChange={e => setToClass(e.target.value)}
                            style={{
                              width: "100%", padding: "8px 10px", borderRadius: 8,
                              border: "1px solid #90CAF9", fontSize: 13, color: "#0D47A1", fontWeight: 600
                            }}
                          >
                            {CLASS_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div style={{ flex: "1 1 120px" }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#0D47A1", display: "block", marginBottom: 4 }}>
                            Section
                          </label>
                          <select
                            value={toSection}
                            onChange={e => setToSection(e.target.value)}
                            style={{
                              width: "100%", padding: "8px 10px", borderRadius: 8,
                              border: "1px solid #90CAF9", fontSize: 13, color: "#0D47A1", fontWeight: 600
                            }}
                          >
                            {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>

                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        marginBottom: 8
                      }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#0D47A1", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={group.students.every(s => selected[s._id])}
                            onChange={e => toggleAll(group, e.target.checked)}
                          />
                          Select all ({group.students.length})
                        </label>
                        <span style={{ fontSize: 11, color: "#2196F3", fontWeight: 700 }}>
                          {selectedCount} selected
                        </span>
                      </div>

                      <div style={{
                        maxHeight: 260, overflowY: "auto", border: "1px solid #E3F2FD",
                        borderRadius: 10, marginBottom: 14
                      }}>
                        {group.students
                          .slice()
                          .sort((a, b) => (a.studentName || "").localeCompare(b.studentName || ""))
                          .map(s => (
                            <label
                              key={s._id}
                              style={{
                                display: "flex", alignItems: "center", gap: 10,
                                padding: "9px 14px", borderBottom: "1px solid #F0F6FF",
                                fontSize: 13, color: "#0D47A1", cursor: "pointer"
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!selected[s._id]}
                                onChange={() => toggleStudent(s._id)}
                              />
                              <span style={{ fontWeight: 600 }}>{s.studentName || "—"}</span>
                              <span style={{ marginLeft: "auto", fontSize: 11, color: "#2196F3" }}>
                                Roll: {s.rollNo || "—"}
                              </span>
                            </label>
                          ))}
                      </div>

                      <button
                        onClick={() => handleTransfer(group)}
                        disabled={transferring}
                        style={{
                          background: "linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)",
                          color: "#FFFFFF", border: "none", padding: "10px 18px", borderRadius: 10,
                          fontSize: 13, fontWeight: 700, cursor: transferring ? "not-allowed" : "pointer",
                          opacity: transferring ? 0.7 : 1, display: "flex", alignItems: "center", gap: 8
                        }}
                      >
                        <i className="fas fa-check"></i>
                        {transferring ? "Transferring…" : `Transfer ${selectedCount} to ${toClass}-${toSection}`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}